import { Fragment } from 'react';
import { Button, Card, Form, Modal, Spinner, Table } from 'react-bootstrap';

import { COMPARECIMENTO_CORES } from '../../constants/relatorio-cores';

import type { RelatorioCivelStepProps } from './relatorio-civel-step-props';

import { TipoPeriodoDescanso } from '@/modules/business/periodo/enums/tipo-periodo-descanso.enum';
import { diasDaSemana } from '@/shared/utils/datas';

/** Cor de fundo do dia por status marcado (dias sem marcação ficam sem cor). */
const CORES_STATUS_DIA: Record<string, string | undefined> = {
  presente: COMPARECIMENTO_CORES.PRESENTE,
  ausente: COMPARECIMENTO_CORES.AUSENTE,
  remoto: COMPARECIMENTO_CORES.REMOTO,
  impedimento: COMPARECIMENTO_CORES.IMPEDIMENTO,
};

export default function Step01Comparecimento(props: RelatorioCivelStepProps) {
  const {
    formData,
    isViewMode,
    origemVisualizacao,
    cardStyle,
    headerStyle,
    justificativaModalDia,
    justificativaTextoTemp,
    setJustificativaTextoTemp,
    justificativaArquivoTemp,
    fileInputRef,
    handleFileChange,
    salvarJustificativa,
    cancelarJustificativa,
    gerarCalendarioMensal,
    contarImpedimentosNoPeriodo,
    podeCorregedoriaAgirComparecimento,
    diasComparecimentoCorregedoriaPendentes,
    corregedoriaDecisaoDiaLoading,
    aplicarDecisaoCorregedoriaNoDia,
    getMesLabel,
    getNomeSemana,
    getStatusDia,
    isDiaUtil,
    isDiaFuturo,
    getImpedimentoDia,
    getFeriadosNaData,
    abrirJustificativaModal,
    atualizarStatusDia,
    getTipoPeriodoNome,
    diaContaComoPresenca,
  } = props;

  const mesNorm = String(formData.mes).padStart(2, '0');
  const calendario = gerarCalendarioMensal(mesNorm, formData.ano);
  const diasStatus = formData.comparecimento.diasStatus || [];
  // `contarImpedimentosNoPeriodo` é usado em outros trechos da etapa; aqui não precisamos armazenar em variável.
  contarImpedimentosNoPeriodo();
  const diasAverbacaoPendentes = diasStatus.some(
    (d) =>
      (d.status === 'ausente' && d.ausenteAceitoCorregedoria !== true) ||
      (d.status === 'remoto' && !diaContaComoPresenca(d)),
  );

  return (
    <Fragment>
      {isViewMode && origemVisualizacao === 'corregedoria' && diasAverbacaoPendentes && (
        <div className="alert alert-warning" role="alert" style={{ borderLeft: '5px solid #ffc107' }}>
          <i className="bi bi-exclamation-triangle text-warning blink-tri me-2" aria-hidden />
          <strong>Atenção:</strong> existem dias marcados como <strong>Remoto</strong> ou <strong>Ausente</strong> sem
          decisão da Corregedoria. Use a tabela abaixo ou o fluxo na lista de relatórios antes do aval final.
        </div>
      )}

      {podeCorregedoriaAgirComparecimento && (
        <Card className="mb-3" style={cardStyle}>
          <Card.Header style={headerStyle}>
            <h6 className="mb-0 text-center">Corregedoria — decisão por dia (remoto / ausência)</h6>
          </Card.Header>
          <Card.Body>
            {diasComparecimentoCorregedoriaPendentes.length === 0 ? (
              <p className="mb-0 text-muted small">
                Não há dias remoto ou ausente pendentes. Você pode voltar à lista da Corregedoria e registrar o aval
                final.
              </p>
            ) : (
              <div className="table-responsive">
                <Table bordered size="sm" className="mb-0 align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>Data</th>
                      <th>Tipo</th>
                      <th>Justificativa</th>
                      <th style={{ minWidth: 200 }}>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {diasComparecimentoCorregedoriaPendentes.map((d) => {
                      const mesR = String(d.mes ?? formData.mes).padStart(2, '0');
                      const anoR = d.ano ?? formData.ano;
                      const rowKey = `${d.dia}-${mesR}-${anoR}`;
                      const busy = corregedoriaDecisaoDiaLoading === rowKey;
                      const labelData = `${d.dia} ${getMesLabel(mesR)}/${anoR}`;
                      return (
                        <tr key={`${d.status}-${rowKey}`}>
                          <td>{labelData}</td>
                          <td>{d.status === 'remoto' ? 'Remoto' : 'Ausente'}</td>
                          <td>
                            <small className="text-muted">{d.justificativaTexto?.trim() || '—'}</small>
                          </td>
                          <td>
                            <div className="d-flex flex-wrap gap-1">
                              {d.status === 'remoto' ? (
                                <>
                                  <Button
                                    size="sm"
                                    variant="success"
                                    disabled={busy}
                                    onClick={() => aplicarDecisaoCorregedoriaNoDia(d, 'remoto_validar')}
                                  >
                                    {busy ? <Spinner animation="border" size="sm" /> : 'Validar presença'}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline-danger"
                                    disabled={busy}
                                    onClick={() => aplicarDecisaoCorregedoriaNoDia(d, 'remoto_rejeitar')}
                                  >
                                    Anular dia
                                  </Button>
                                </>
                              ) : (
                                <>
                                  <Button
                                    size="sm"
                                    variant="success"
                                    disabled={busy}
                                    onClick={() => aplicarDecisaoCorregedoriaNoDia(d, 'ausencia_aceitar')}
                                  >
                                    {busy ? <Spinner animation="border" size="sm" /> : 'Aceitar ausência'}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline-danger"
                                    disabled={busy}
                                    onClick={() => aplicarDecisaoCorregedoriaNoDia(d, 'ausencia_rejeitar')}
                                  >
                                    Anular dia
                                  </Button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      {/* Calendário Mensal */}
      <Card className="mb-4" style={cardStyle}>
        <Card.Header style={headerStyle}>
          <h6 className="mb-0 text-center">
            {getMesLabel(formData.mes)}/{formData.ano}
          </h6>
        </Card.Header>
        <Card.Body className="p-0">
          <Table bordered className="mb-0" style={{ tableLayout: 'fixed' }}>
            <thead>
              <tr className="table-light">
                <th style={{ width: '120px', textAlign: 'center', fontWeight: 'bold' }}>Semana</th>
                {diasDaSemana.map((dia) => (
                  <th
                    key={dia}
                    style={{
                      textAlign: 'center',
                      fontWeight: 'bold',
                    }}
                  >
                    {dia}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {calendario.map((semana, semanaIndex: number) => {
                return (
                  <tr key={semanaIndex}>
                    <td
                      style={{
                        textAlign: 'center',
                        fontWeight: 'bold',
                        verticalAlign: 'middle',
                        fontSize: '0.85rem',
                      }}
                    >
                      <div>{getNomeSemana(semanaIndex)}</div>
                    </td>
                    {semana.map((dia, diaIndex: number) => {
                      const isDiaUtilAtual = dia !== null && isDiaUtil(dia.dia, dia.mes, dia.ano);
                      const isFuturo = dia !== null && isDiaFuturo(dia.dia, dia.mes, dia.ano);
                      const statusDia = dia !== null ? getStatusDia(dia.dia, dia.mes, dia.ano) : undefined;
                      const isMesAtual = dia !== null ? dia.isMesAtual : false;
                      const impedimentoDia = dia !== null ? getImpedimentoDia(dia.dia, dia.mes, dia.ano) : null;
                      const temImpedimento = impedimentoDia !== null;

                      // Verificar se o dia é feriado/bloqueio (para destacar em amarelo)
                      const feriadosNaData =
                        dia !== null ? getFeriadosNaData(new Date(dia.ano, parseInt(dia.mes) - 1, dia.dia)) : [];
                      const temFeriado = feriadosNaData.length > 0;
                      const temFerias = impedimentoDia?.tipo === TipoPeriodoDescanso.Ferias;
                      const temDiaSemObrigacao = temFeriado || temFerias;

                      const valorSelectAtual = temImpedimento ? 'impedimento' : statusDia?.status || 'nao_marcado';
                      const corStatusDia = CORES_STATUS_DIA[valorSelectAtual];
                      // Eventos do cadastro do defensor (férias, abonos, folgas, licenças) e feriados no dia
                      const nomeImpedimento = impedimentoDia ? getTipoPeriodoNome(impedimentoDia.tipo) : '';
                      const tituloDiaEspecial =
                        [
                          ...feriadosNaData.map((f) => f.nome),
                          ...(nomeImpedimento
                            ? [
                                impedimentoDia?.motivo?.trim()
                                  ? `${nomeImpedimento}: ${impedimentoDia.motivo.trim()}`
                                  : nomeImpedimento,
                              ]
                            : []),
                        ].join(', ') || undefined;

                      return (
                        <td
                          key={diaIndex}
                          style={{
                            textAlign: 'center',
                            padding: '8px 4px',
                            verticalAlign: 'middle',
                            opacity: !isMesAtual ? 0.5 : 1,
                            backgroundColor: temDiaSemObrigacao
                              ? COMPARECIMENTO_CORES.DIA_SEM_OBRIGACAO_FUNDO
                              : undefined,
                          }}
                          title={tituloDiaEspecial}
                        >
                          {dia !== null && (
                            <div className="d-flex flex-column align-items-center">
                              <span style={{ fontSize: '1rem', fontWeight: 'bold' }}>{dia.dia}</span>

                              {/* Select para dias úteis não futuros - apenas do mês atual */}
                              {/* Mostrar select apenas para dias do período do relatório atual */}
                              {isDiaUtilAtual && !isFuturo && isMesAtual && !isViewMode && (
                                <Form.Select
                                  size="sm"
                                  value={valorSelectAtual}
                                  onChange={(e) => {
                                    const valor = e.target.value as
                                      | 'presente'
                                      | 'ausente'
                                      | 'remoto'
                                      | 'impedimento'
                                      | 'nao_marcado';
                                    if (valor === 'ausente') {
                                      abrirJustificativaModal(dia.dia, dia.mes, dia.ano);
                                    } else if (valor === 'impedimento') {
                                      // Se tem impedimento cadastrado, usar ele
                                      if (impedimentoDia) {
                                        atualizarStatusDia(
                                          dia.dia,
                                          'impedimento',
                                          undefined,
                                          null,
                                          impedimentoDia.tipo,
                                          getTipoPeriodoNome(impedimentoDia.tipo),
                                          dia.mes,
                                          dia.ano,
                                        );
                                      }
                                    } else {
                                      atualizarStatusDia(
                                        dia.dia,
                                        valor,
                                        undefined,
                                        undefined,
                                        undefined,
                                        undefined,
                                        dia.mes,
                                        dia.ano,
                                      );
                                    }
                                  }}
                                  disabled={temImpedimento}
                                  style={{
                                    fontSize: '0.65rem',
                                    padding: '2px 4px',
                                    marginTop: '4px',
                                    width: '95%',
                                    borderRadius: '4px',
                                    textAlign: 'center',
                                    fontWeight: 'bold',
                                    backgroundColor: corStatusDia,
                                    color: corStatusDia ? '#fff' : undefined,
                                  }}
                                >
                                  <option value="nao_marcado">--</option>
                                  <option value="presente">Presente</option>
                                  <option value="ausente">Ausente</option>
                                  <option value="remoto">Remoto</option>
                                  {temImpedimento && (
                                    <option value="impedimento">{getTipoPeriodoNome(impedimentoDia.tipo)}</option>
                                  )}
                                </Form.Select>
                              )}
                              {/* Visualização somente leitura (aprovação / devolução / Corregedoria) */}
                              {isDiaUtilAtual && !isFuturo && isMesAtual && isViewMode && (
                                <div
                                  style={{
                                    fontSize: '0.65rem',
                                    marginTop: '4px',
                                    padding: '2px 4px',
                                    width: '95%',
                                    borderRadius: '4px',
                                    fontWeight: 'bold',
                                    textAlign: 'center',
                                    backgroundColor: corStatusDia ?? COMPARECIMENTO_CORES.NAO_MARCADO,
                                    color: corStatusDia ? '#fff' : COMPARECIMENTO_CORES.IMPEDIMENTO,
                                  }}
                                  title={
                                    temImpedimento
                                      ? tituloDiaEspecial
                                      : statusDia?.justificativaTexto?.trim() || undefined
                                  }
                                >
                                  {valorSelectAtual === 'presente' && 'Presente'}
                                  {valorSelectAtual === 'ausente' && 'Ausente'}
                                  {valorSelectAtual === 'remoto' && 'Remoto'}
                                  {valorSelectAtual === 'impedimento' && (nomeImpedimento || 'Impedimento')}
                                  {valorSelectAtual === 'nao_marcado' && '--'}
                                </div>
                              )}
                              {/* Feriado ou evento do defensor (férias, abono, folga, licença, afastamento) */}
                              {(temFeriado || temImpedimento) && (
                                <small
                                  className="text-truncate"
                                  style={{
                                    fontSize: '0.65rem',
                                    marginTop: '4px',
                                    maxWidth: '100%',
                                    fontWeight: 600,
                                    color: temDiaSemObrigacao
                                      ? COMPARECIMENTO_CORES.DIA_SEM_OBRIGACAO_TEXTO
                                      : COMPARECIMENTO_CORES.IMPEDIMENTO,
                                  }}
                                  title={tituloDiaEspecial}
                                >
                                  {temImpedimento ? (
                                    <>
                                      <i className="bi bi-calendar-x me-1" aria-hidden />
                                      {nomeImpedimento}
                                    </>
                                  ) : (
                                    'Feriado'
                                  )}
                                </small>
                              )}
                              {/* Mostrar apenas visualização para dias de outros períodos */}
                              {isDiaUtilAtual && !isFuturo && !isMesAtual && (
                                <div
                                  className="text-muted"
                                  style={{
                                    fontSize: '0.65rem',
                                    marginTop: '4px',
                                    fontStyle: 'italic',
                                    cursor: 'not-allowed',
                                  }}
                                  title={
                                    statusDia?.relatorioOrigemPeriodo
                                      ? `Preenchido no relatório ${statusDia.relatorioOrigemPeriodo} (somente leitura)`
                                      : `Este dia pertence ao período de ${getMesLabel(dia.mes)}/${dia.ano} e não pode ser editado no relatório de ${getMesLabel(formData.mes)}/${formData.ano}`
                                  }
                                >
                                  {statusDia?.status === 'presente' && '✓ Presente'}
                                  {statusDia?.status === 'ausente' && '✗ Ausente'}
                                  {statusDia?.status === 'remoto' && '◐ Remoto'}
                                  {statusDia?.status === 'impedimento' && '⊘ Impedimento'}
                                  {!statusDia && '--'}
                                </div>
                              )}
                              {/* Ícone de justificativa - apenas para dias do período atual */}
                              {statusDia?.status === 'ausente' &&
                                (statusDia.justificativaTexto || statusDia.justificativaArquivo) &&
                                isMesAtual &&
                                !isViewMode && (
                                  <Button
                                    variant="link"
                                    size="sm"
                                    className="p-0 mt-1"
                                    onClick={() => abrirJustificativaModal(dia.dia, dia.mes, dia.ano)}
                                    title="Ver/Editar justificativa"
                                  >
                                    <i className="bi bi-file-text"></i>
                                  </Button>
                                )}
                              {isViewMode &&
                                statusDia?.status === 'ausente' &&
                                statusDia.justificativaTexto?.trim() &&
                                isMesAtual && (
                                  <small
                                    className="text-muted mt-1"
                                    style={{ fontSize: '0.55rem', maxWidth: '100%', lineHeight: 1.2 }}
                                    title={statusDia.justificativaTexto}
                                  >
                                    <i className="bi bi-file-text me-1" aria-hidden />
                                    Justificativa
                                  </small>
                                )}

                              {/* Indicador de dia futuro */}
                              {isFuturo && (
                                <small style={{ fontSize: '0.6rem' }}>
                                  <i className="bi bi-lock"></i>
                                </small>
                              )}
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Modal
        show={justificativaModalDia !== null}
        onHide={cancelarJustificativa}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton className="bg-warning text-dark">
          <Modal.Title>
            <i className="bi bi-file-earmark-text me-2"></i>
            Justificativa de Ausência - Dia {justificativaModalDia}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label className="fw-bold">Texto da Justificativa</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Descreva o motivo da ausência..."
              value={justificativaTextoTemp}
              onChange={(e) => setJustificativaTextoTemp(e.target.value)}
              style={{ borderRadius: '8px' }}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="fw-bold">Arquivo de Comprovação</Form.Label>
            <Form.Control type="file" ref={fileInputRef} onChange={handleFileChange} style={{ borderRadius: '8px' }} />
            {justificativaArquivoTemp && (
              <small className="text-muted d-block mt-1">
                <i className="bi bi-paperclip me-1"></i>
                {justificativaArquivoTemp.name}
              </small>
            )}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={cancelarJustificativa} style={{ borderRadius: '8px' }}>
            <i className="bi bi-x-lg me-1"></i>Cancelar
          </Button>
          <Button variant="warning" size="sm" onClick={salvarJustificativa} style={{ borderRadius: '8px' }}>
            <i className="bi bi-check-lg me-1"></i>Salvar Ausência
          </Button>
        </Modal.Footer>
      </Modal>
    </Fragment>
  );
}

